import React, { useState, useEffect, useCallback, useRef } from 'react';
import { showSuccess, showError } from '../utils/alertas';

const IS_PROD = true;
const API_BASE_URL = IS_PROD ? 'https://sigoapi.jhe-etes.com.br/api' : 'http://localhost:3001/api';

function ConfiguracoesMapaModal({ isOpen, onClose, sidebarConfig, setSidebarConfig, gerenciasOcultas = [], setGerenciasOcultas, onGerenciasUpdated }) {
    const [activeTab, setActiveTab] = useState('sidebar'); // 'sidebar' ou 'gerencias'
    
    // Estados da Gestão de Gerências
    const [gerencias, setGerencias] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editingGerencia, setEditingGerencia] = useState(null); // null = criando
    const [formGerencia, setFormGerencia] = useState({
        nome: '',
        responsavel: '',
        cor_hex: '#0C336A',
        status: 'ativo'
    });

    // Estados dos Logs de Auditoria
    const [logs, setLogs] = useState([]);
    const [usuariosLogs, setUsuariosLogs] = useState([]);
    const [loadingLogs, setLoadingLogs] = useState(false);
    const [filtroUsuario, setFiltroUsuario] = useState('todos');
    const [filtroDataInicio, setFiltroDataInicio] = useState('');
    const [filtroDataFim, setFiltroDataFim] = useState('');

    const onGerenciasUpdatedRef = useRef(onGerenciasUpdated);
    useEffect(() => {
        onGerenciasUpdatedRef.current = onGerenciasUpdated;
    }, [onGerenciasUpdated]);

    const fetchGerencias = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/gerencias`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });
            if (res.ok) {
                const data = await res.json();
                setGerencias(data);
                if (onGerenciasUpdatedRef.current) {
                    onGerenciasUpdatedRef.current(data);
                }
            }
        } catch (err) {
            console.error("Erro ao buscar gerências:", err);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchLogs = useCallback(async () => {
        // Só exibe o estado de carregamento completo se ainda não existirem logs na tela
        setLogs(prev => {
            if (prev.length === 0) setLoadingLogs(true);
            return prev;
        });
        try {
            const token = localStorage.getItem('token');
            const queryParams = new URLSearchParams();
            if (filtroUsuario && filtroUsuario !== 'todos') queryParams.append('usuario', filtroUsuario);
            if (filtroDataInicio) queryParams.append('data_inicio', filtroDataInicio);
            if (filtroDataFim) queryParams.append('data_fim', filtroDataFim);

            const res = await fetch(`${API_BASE_URL}/logs?${queryParams.toString()}`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });
            if (res.ok) {
                const data = await res.json();
                setLogs(data);
            }
        } catch (err) {
            console.error("Erro ao buscar logs:", err);
        } finally {
            setLoadingLogs(false);
        }
    }, [filtroUsuario, filtroDataInicio, filtroDataFim]);

    const fetchUsuariosLogs = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/logs/usuarios`, {
                headers: token ? { 'Authorization': `Bearer ${token}` } : {}
            });
            if (res.ok) {
                const data = await res.json();
                setUsuariosLogs(data);
            }
        } catch (err) {
            console.error("Erro ao buscar usuários dos logs:", err);
        }
    }, []);

    useEffect(() => {
        if (!isOpen) return;
        fetchGerencias();
        fetchUsuariosLogs();
    }, [isOpen, fetchGerencias, fetchUsuariosLogs]);

    useEffect(() => {
        if (!isOpen || activeTab !== 'logs') return;
        fetchLogs();
    }, [isOpen, activeTab, fetchLogs]);

    const handleExportPDF = () => {
        const dataHoraExportacao = new Date().toLocaleString('pt-BR');
        let userLogado = 'Administrador SIGO';
        try {
            const userObj = JSON.parse(localStorage.getItem('user') || '{}');
            if (userObj && userObj.nome) {
                userLogado = userObj.nome;
            } else if (localStorage.getItem('usuario_nome')) {
                userLogado = localStorage.getItem('usuario_nome');
            }
        } catch (e) {}

        const windowPrint = window.open('', '_blank', 'width=1000,height=700');
        if (!windowPrint) {
            showError("Atenção", "Bloqueador de popups detectado. Habilite popups no seu navegador para visualizar e salvar o PDF.");
            return;
        }

        const filtroUserStr = filtroUsuario === 'todos' ? 'Todos os Usuários' : filtroUsuario;
        const dataIniStr = filtroDataInicio ? new Date(filtroDataInicio + 'T00:00:00').toLocaleDateString('pt-BR') : 'Início do Histórico';
        const dataFimStr = filtroDataFim ? new Date(filtroDataFim + 'T00:00:00').toLocaleDateString('pt-BR') : 'Hoje';

        const contentHtml = `
            <!DOCTYPE html>
            <html lang="pt-BR">
            <head>
                <meta charset="UTF-8">
                <title>Relatório de Logs de Auditoria - SIGO Sabesp</title>
                <style>
                    @page { size: A4 landscape; margin: 10mm; }
                    body { font-family: 'Segoe UI', Arial, sans-serif; color: #1e293b; margin: 0; padding: 15px; background: #fff; font-size: 11px; }
                    .header { background: #0C336A; color: #ffffff; padding: 18px 24px; border-radius: 10px; margin-bottom: 16px; display: flex; justify-content: space-between; align-items: center; }
                    .header h1 { margin: 0; font-size: 18px; font-weight: 800; }
                    .header p { margin: 3px 0 0 0; font-size: 11px; opacity: 0.85; }
                    .badge { background: rgba(255,255,255,0.2); padding: 4px 10px; border-radius: 12px; font-size: 9px; font-weight: bold; text-transform: uppercase; letter-spacing: 0.5px; }
                    
                    .meta-box { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px 16px; margin-bottom: 16px; display: grid; grid-template-columns: repeat(5, 1fr); gap: 10px; }
                    .meta-item { display: flex; flex-direction: column; }
                    .meta-label { font-size: 9px; text-transform: uppercase; font-weight: 700; color: #64748b; margin-bottom: 2px; }
                    .meta-value { font-size: 11px; font-weight: 700; color: #0C336A; }

                    table { width: 100%; border-collapse: collapse; margin-top: 10px; }
                    th { background: #0C336A; color: #ffffff; text-align: left; padding: 8px 10px; font-size: 9.5px; font-weight: 700; text-transform: uppercase; }
                    td { padding: 8px 10px; border-bottom: 1px solid #e2e8f0; vertical-align: top; font-size: 10px; }
                    tr:nth-child(even) { background-color: #f8fafc; }
                    .user-badge { font-weight: 700; color: #0C336A; background: #e0f2fe; padding: 2px 6px; border-radius: 4px; display: inline-block; }
                    .action-badge { font-weight: 700; color: #0284c7; font-size: 9px; }
                    .details-cell { font-family: monospace; font-size: 9.5px; color: #334155; word-break: break-word; }
                    
                    .footer { margin-top: 20px; padding-top: 10px; border-top: 1px solid #cbd5e1; text-align: center; font-size: 9px; color: #94a3b8; display: flex; justify-content: space-between; }
                </style>
            </head>
            <body>
                <div class="header">
                    <div>
                        <h1>SABESP | SIGO – Relatório Auditável de Logs e Alterações</h1>
                        <p>Sistema Integrado de Gestão Operacional</p>
                    </div>
                    <div class="badge">Registro de Auditoria</div>
                </div>

                <div class="meta-box">
                    <div class="meta-item">
                        <span class="meta-label">📅 Exportado em</span>
                        <span class="meta-value">${dataHoraExportacao}</span>
                    </div>
                    <div class="meta-item">
                        <span class="meta-label">👤 Exportado por</span>
                        <span class="meta-value">${userLogado}</span>
                    </div>
                    <div class="meta-item">
                        <span class="meta-label">🔍 Filtro Usuário</span>
                        <span class="meta-value">${filtroUserStr}</span>
                    </div>
                    <div class="meta-item">
                        <span class="meta-label">🗓️ Período Filtrado</span>
                        <span class="meta-value">${dataIniStr} até ${dataFimStr}</span>
                    </div>
                    <div class="meta-item">
                        <span class="meta-label">📊 Total Registros</span>
                        <span class="meta-value">${logs.length} registros</span>
                    </div>
                </div>

                <table>
                    <thead>
                        <tr>
                            <th style="width: 130px;">Data & Hora</th>
                            <th style="width: 140px;">Usuário</th>
                            <th style="width: 150px;">Ação / Módulo</th>
                            <th>Detalhamento da Alteração (De ➔ Para)</th>
                        </tr>
                    </thead>
                    <tbody>
                        ${logs.map(log => `
                            <tr>
                                <td style="white-space: nowrap; font-weight: bold; color: #475569;">
                                    ${new Date(log.data_hora).toLocaleString('pt-BR')}
                                </td>
                                <td><span class="user-badge">${log.usuario_nome || 'Sistema'}</span></td>
                                <td>
                                    <div style="font-weight: bold;">${log.pagina_referencia || 'Geral'}</div>
                                    <span class="action-badge">${log.tipo_acao || 'ALTERAÇÃO'}</span>
                                </td>
                                <td class="details-cell">${log.detalhes || '-'}</td>
                            </tr>
                        `).join('')}
                    </tbody>
                </table>

                <div class="footer">
                    <span>SABESP SIGO &copy; ${new Date().getFullYear()} – Documento de Auditoria Interna.</span>
                    <span>Emitido por ${userLogado}</span>
                </div>

                <script>
                    window.onload = function() {
                        setTimeout(() => {
                            window.print();
                        }, 400);
                    }
                </script>
            </body>
            </html>
        `;

        windowPrint.document.write(contentHtml);
        windowPrint.document.close();
    };

    const handleToggleSidebarItem = (key) => {
        setSidebarConfig(prev => ({
            ...prev,
            [key]: !prev[key]
        }));
    };

    const toggleVisibilidadeGerencia = (g) => {
        const gerenciaNome = g.nome || g;
        setGerenciasOcultas(prev => {
            const list = Array.isArray(prev) ? prev : [];
            if (list.includes(gerenciaNome)) {
                return list.filter(nome => nome !== gerenciaNome);
            } else {
                return [...list, gerenciaNome];
            }
        });
    };

    const handleSaveGerencia = async (e) => {
        e.preventDefault();
        if (!formGerencia.nome.trim()) {
            showError("Atenção", "O nome da gerência é obrigatório.");
            return;
        }

        try {
            const token = localStorage.getItem('token');
            const isEdit = !!editingGerencia;
            const url = isEdit ? `${API_BASE_URL}/gerencias/${editingGerencia.id}` : `${API_BASE_URL}/gerencias`;
            const method = isEdit ? 'PUT' : 'POST';

            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                body: JSON.stringify(formGerencia)
            });

            if (res.ok) {
                showSuccess("Sucesso!", isEdit ? "Gerência atualizada com sucesso!" : "Gerência criada com sucesso!");
                setFormGerencia({ nome: '', responsavel: '', cor_hex: '#0C336A', status: 'ativo' });
                setEditingGerencia(null);
                fetchGerencias();
            } else {
                let msgErro = "Não foi possível salvar a gerência.";
                try {
                    const data = await res.json();
                    if (data && (data.message || data.error)) {
                        msgErro = data.message || data.error;
                    }
                } catch (e) {}
                showError("Erro no Servidor", `${msgErro} (Status ${res.status})`);
            }
        } catch (err) {
            console.error("Erro ao salvar gerência:", err);
            showError("Erro", "Falha de conexão com o servidor.");
        }
    };

    const startEditGerencia = (g) => {
        setEditingGerencia(g);
        setFormGerencia({
            nome: g.nome || '',
            responsavel: g.responsavel || '',
            cor_hex: g.cor_hex || '#0C336A',
            status: g.status || 'ativo'
        });
    };

    const cancelEdit = () => {
        setEditingGerencia(null);
        setFormGerencia({ nome: '', responsavel: '', cor_hex: '#0C336A', status: 'ativo' });
    };

    // eslint-disable-next-line no-unused-vars
    const toggleStatusGerencia = async (g) => {
        const novoStatus = g.status === 'ativo' ? 'inativo' : 'ativo';
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_BASE_URL}/gerencias/${g.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': `Bearer ${token}` } : {})
                },
                body: JSON.stringify({ status: novoStatus })
            });
            if (res.ok) {
                fetchGerencias();
            } else {
                showError("Erro no Servidor", `Não foi possível alterar o status (Status ${res.status}).`);
            }
        } catch (err) {
            console.error("Erro ao alterar status:", err);
            showError("Erro", "Falha de conexão com o servidor.");
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-sm p-3 sm:p-4 animate-fadeIn">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-3xl max-h-[85vh] sm:max-h-[90vh] overflow-hidden flex flex-col border border-white/20">
                
                {/* Cabeçalho */}
                <div className="bg-slate-900 text-white px-5 sm:px-6 py-4 sm:py-5 flex justify-between items-center shrink-0">
                    <div className="flex items-center gap-2.5">
                        <span className="text-xl sm:text-2xl">⚙️</span>
                        <div>
                            <h2 className="text-base sm:text-lg font-black tracking-tight">Configurações & Customização</h2>
                            <p className="text-[10px] sm:text-xs text-blue-200/80 font-medium">Visibilidade da barra lateral e gestão de gerências</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-white/10 text-white/70 hover:text-white transition-all text-lg font-bold shrink-0">
                        ✕
                    </button>
                </div>

                {/* Abas de Navegação Responsivas (flex-col sm:flex-row) */}
                <div className="bg-slate-100 p-2 flex flex-col sm:flex-row gap-2 border-b border-gray-200 shrink-0">
                    <button
                        onClick={() => setActiveTab('sidebar')}
                        className={`w-full sm:flex-1 py-2.5 px-3.5 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-2 ${
                            activeTab === 'sidebar' ? 'bg-white text-sabesp-blue shadow-sm' : 'text-gray-500 hover:text-gray-800'
                        }`}
                    >
                        <span>🎛️</span> Visualização do Menu
                    </button>
                    <button
                        onClick={() => setActiveTab('gerencias')}
                        className={`w-full sm:flex-1 py-2.5 px-3.5 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-2 ${
                            activeTab === 'gerencias' ? 'bg-white text-sabesp-blue shadow-sm' : 'text-gray-500 hover:text-gray-800'
                        }`}
                    >
                        <span>🏢</span> Gestão de Gerências
                    </button>
                    <button
                        onClick={() => setActiveTab('logs')}
                        className={`w-full sm:flex-1 py-2.5 px-3.5 rounded-xl text-xs font-black transition-all flex items-center justify-center gap-2 ${
                            activeTab === 'logs' ? 'bg-white text-sabesp-blue shadow-sm' : 'text-gray-500 hover:text-gray-800'
                        }`}
                    >
                        <span>📜</span> Registro de Logs (Auditoria)
                    </button>
                </div>

                {/* Conteúdo com Scroll Próprio */}
                <div className="p-4 sm:p-6 overflow-y-auto custom-scrollbar flex-1 bg-slate-50/50">
                    
                    {/* ABA 1: VISUALIZAÇÃO DO MENU LATERAL */}
                    {activeTab === 'sidebar' && (
                        <div className="space-y-4">
                            <div className="bg-blue-50/70 border border-blue-100 p-3.5 rounded-2xl text-xs text-sabesp-blue font-medium mb-3">
                                💡 Ative ou desative os switches abaixo para exibir ou ocultar elementos da barra lateral em tempo real sem afetar os filtros do mapa.
                            </div>

                            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm divide-y divide-gray-100">
                                {[
                                    { key: 'showNovaETE', title: '➕ Botão Nova ETE', desc: 'Botão para criação de ETE (Apenas visível logado)' },
                                    { key: 'showPesquisa', title: '🔍 Campo de Pesquisa Rápida', desc: 'Permite buscar ETEs por nome, contrato ou cidade' },
                                    { key: 'showMultiSelect', title: '🚰 Filtro por ETEs (Seleção Múltipla)', desc: 'Dropdown compacto para selecionar uma ou várias ETEs' },
                                    { key: 'showGerencia', title: '📑 Filtro por Gerência', desc: 'Seletor de gerência cadastrada' },
                                    { key: 'showMunicipio', title: '🏙️ Filtro por Municípios', desc: 'Seletor de municípios com obras ativas' },
                                    { key: 'showTipoMapa', title: '🛰️ Seletor de Tipo de Mapa (Padrão vs Satélite)', desc: 'Botões alternadores da camada do Leaflet' },
                                    { key: 'showLimparFiltros', title: '🧹 Botão Limpar Filtros', desc: 'Botão rápido para resetar todos os filtros' },
                                ].map((item) => (
                                    <div key={item.key} className="p-3.5 sm:p-4 flex items-center justify-between gap-3 hover:bg-slate-50/60 transition-colors">
                                        <div className="pr-2">
                                            <h4 className="text-xs font-bold text-slate-800">{item.title}</h4>
                                            <p className="text-[10px] sm:text-[11px] text-gray-400 font-medium mt-0.5">{item.desc}</p>
                                        </div>

                                        {/* Switch Toggle */}
                                        <button
                                            type="button"
                                            onClick={() => handleToggleSidebarItem(item.key)}
                                            className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                                sidebarConfig[item.key] ? 'bg-sabesp-cyan' : 'bg-gray-200'
                                            }`}
                                        >
                                            <span
                                                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                                    sidebarConfig[item.key] ? 'translate-x-5' : 'translate-x-0'
                                                }`}
                                            />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ABA 2: GESTÃO DE GERÊNCIAS */}
                    {activeTab === 'gerencias' && (
                        <div className="space-y-5">
                            
                            {/* Formulário de Cadastro / Edição com Grid Responsivo */}
                            <form onSubmit={handleSaveGerencia} className="bg-white p-4 sm:p-5 rounded-2xl border border-gray-100 shadow-sm space-y-4">
                                <h3 className="text-xs font-black text-slate-800 uppercase tracking-wider flex items-center justify-between">
                                    <span>{editingGerencia ? `✏️ Editando: ${editingGerencia.nome}` : '➕ Cadastrar Nova Gerência'}</span>
                                    {editingGerencia && (
                                        <button type="button" onClick={cancelEdit} className="text-[10px] text-red-500 font-bold hover:underline">
                                            Cancelar Edição
                                        </button>
                                    )}
                                </h3>

                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Nome da Gerência *</label>
                                        <input
                                            type="text"
                                            placeholder="Ex: Localizada 3"
                                            value={formGerencia.nome}
                                            onChange={(e) => setFormGerencia(prev => ({ ...prev, nome: e.target.value }))}
                                            className="w-full p-2.5 bg-slate-50 border border-gray-200 rounded-xl text-xs font-semibold focus:outline-none focus:border-sabesp-cyan"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Responsável</label>
                                        <input
                                            type="text"
                                            placeholder="Ex: Eng. João Silva"
                                            value={formGerencia.responsavel}
                                            onChange={(e) => setFormGerencia(prev => ({ ...prev, responsavel: e.target.value }))}
                                            className="w-full p-2.5 bg-slate-50 border border-gray-200 rounded-xl text-xs font-semibold focus:outline-none focus:border-sabesp-cyan"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-400 uppercase mb-1">Cor Hexadecimal do Pin</label>
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="color"
                                                value={formGerencia.cor_hex}
                                                onChange={(e) => setFormGerencia(prev => ({ ...prev, cor_hex: e.target.value }))}
                                                className="w-10 h-9 p-0.5 bg-white border border-gray-200 rounded-lg cursor-pointer shrink-0"
                                            />
                                            <input
                                                type="text"
                                                value={formGerencia.cor_hex}
                                                onChange={(e) => setFormGerencia(prev => ({ ...prev, cor_hex: e.target.value }))}
                                                className="w-full p-2.5 bg-slate-50 border border-gray-200 rounded-xl text-xs font-mono font-bold uppercase"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="flex justify-end pt-1">
                                    <button
                                        type="submit"
                                        className="px-6 py-2.5 bg-sabesp-blue hover:bg-blue-900 text-white font-bold text-xs rounded-xl shadow-sm transition-all"
                                    >
                                        {editingGerencia ? 'Atualizar Gerência' : 'Salvar Gerência'}
                                    </button>
                                </div>
                            </form>

                            {/* Tabela de Gerências Cadastradas */}
                            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                                <div className="p-4 bg-slate-100/70 border-b border-gray-100 flex justify-between items-center">
                                    <h4 className="text-xs font-black text-slate-800 uppercase tracking-wider">Gerências Cadastradas ({gerencias.length})</h4>
                                    <span className="text-[10px] text-gray-400 font-bold">Cores vinculadas dinamicamente aos Pins do Mapa</span>
                                </div>

                                {loading ? (
                                    <div className="p-8 text-center text-xs font-bold text-gray-400">Carregando gerências...</div>
                                ) : gerencias.length === 0 ? (
                                    <div className="p-8 text-center text-xs font-medium text-gray-400">Nenhuma gerência cadastrada.</div>
                                ) : (
                                    <div className="divide-y divide-gray-100">
                                        {gerencias.map((g) => {
                                            const isOculta = (gerenciasOcultas || []).includes(g.nome);
                                            return (
                                                <div key={g.id} className="p-3.5 flex items-center justify-between gap-4 hover:bg-slate-50 transition-colors">
                                                    <div className="flex items-center gap-3">
                                                        <span
                                                            className="w-6 h-6 rounded-full border-2 border-white shadow-sm shrink-0"
                                                            style={{ backgroundColor: g.cor_hex || '#0C336A' }}
                                                            title={`Cor: ${g.cor_hex}`}
                                                        />
                                                        <div>
                                                            <span className={`text-xs font-extrabold block ${isOculta ? 'text-gray-400 line-through' : 'text-slate-800'}`}>{g.nome}</span>
                                                            <span className="text-[10px] text-gray-400 font-medium">{g.responsavel || 'Sem Responsável'}</span>
                                                        </div>
                                                    </div>

                                                    <div className="flex items-center gap-3">
                                                        {/* Switch de Visibilidade no Mapa */}
                                                        <button
                                                            type="button"
                                                            onClick={() => toggleVisibilidadeGerencia(g)}
                                                            className={`flex items-center gap-1.5 text-[10px] font-bold px-3 py-1.5 rounded-xl uppercase transition-all ${
                                                                !isOculta 
                                                                    ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100' 
                                                                    : 'bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100'
                                                            }`}
                                                            title={!isOculta ? "Ocultar gerência e marcadores do mapa" : "Exibir gerência e marcadores no mapa"}
                                                        >
                                                            <span>{!isOculta ? '👁️ Visível no Mapa' : '🙈 Oculta no Mapa'}</span>
                                                        </button>

                                                        <button
                                                            type="button"
                                                            onClick={() => startEditGerencia(g)}
                                                            className="text-xs text-sabesp-cyan hover:underline font-bold px-2 py-1"
                                                        >
                                                            ✏️ Editar
                                                        </button>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                        </div>
                    )}

                    {/* ABA 3: REGISTRO DE LOGS DE AUDITORIA */}
                    {activeTab === 'logs' && (
                        <div className="space-y-4">
                            {/* Painel de Filtros e Botão Exportar PDF */}
                            <div className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm space-y-3">
                                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-gray-100 pb-3">
                                    <div>
                                        <h3 className="text-xs font-black text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                                            <span>🔍</span> Filtros de Auditoria
                                        </h3>
                                        <p className="text-[10px] text-gray-400 font-medium mt-0.5">Filtre por usuário específico e intervalo de datas para exportação em PDF</p>
                                    </div>
                                    <button
                                        onClick={handleExportPDF}
                                        disabled={logs.length === 0}
                                        className="w-full sm:w-auto px-4 py-2 bg-sabesp-blue hover:bg-blue-900 disabled:opacity-50 text-white rounded-xl text-xs font-bold transition-all shadow-sm flex items-center justify-center gap-1.5 cursor-pointer"
                                    >
                                        <span>🖨️</span> Exportar PDF Personalizado
                                    </button>
                                </div>

                                {/* Inputs de Filtro */}
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1">
                                    {/* Usuário */}
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Usuário</label>
                                        <select
                                            value={filtroUsuario}
                                            onChange={(e) => setFiltroUsuario(e.target.value)}
                                            className="w-full p-2 bg-slate-50 border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 focus:outline-none focus:border-sabesp-blue"
                                        >
                                            <option value="todos">👤 Todos os Usuários</option>
                                            {usuariosLogs.map((u) => (
                                                <option key={u} value={u}>{u}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Data Início */}
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Data Início</label>
                                        <input
                                            type="date"
                                            value={filtroDataInicio}
                                            onChange={(e) => setFiltroDataInicio(e.target.value)}
                                            className="w-full p-2 bg-slate-50 border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 focus:outline-none focus:border-sabesp-blue"
                                        />
                                    </div>

                                    {/* Data Fim */}
                                    <div>
                                        <label className="block text-[10px] font-bold text-gray-500 uppercase mb-1">Data Fim</label>
                                        <input
                                            type="date"
                                            value={filtroDataFim}
                                            onChange={(e) => setFiltroDataFim(e.target.value)}
                                            className="w-full p-2 bg-slate-50 border border-gray-200 rounded-xl text-xs font-semibold text-gray-700 focus:outline-none focus:border-sabesp-blue"
                                        />
                                    </div>
                                </div>

                                {(filtroUsuario !== 'todos' || filtroDataInicio || filtroDataFim) && (
                                    <div className="flex justify-end pt-1">
                                        <button
                                            onClick={() => {
                                                setFiltroUsuario('todos');
                                                setFiltroDataInicio('');
                                                setFiltroDataFim('');
                                            }}
                                            className="text-[11px] font-bold text-red-500 hover:text-red-700 underline"
                                        >
                                            🧹 Limpar Filtros Aplicados
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Tabela de Logs */}
                            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                                <div className="p-3 bg-slate-100 border-b border-gray-200 flex justify-between items-center">
                                    <span className="text-xs font-extrabold text-slate-700 uppercase tracking-wider">Registros Encontrados ({logs.length})</span>
                                    <span className="text-[10px] font-medium text-gray-400">Ordenado por data mais recente</span>
                                </div>

                                {loadingLogs && logs.length === 0 ? (
                                    <div className="p-12 text-center text-xs font-bold text-gray-400">Carregando registros de auditoria...</div>
                                ) : logs.length === 0 ? (
                                    <div className="p-12 text-center text-xs font-medium text-gray-400">
                                        Nenhum registro de log encontrado para os filtros selecionados.
                                    </div>
                                ) : (
                                    <div className={`overflow-x-auto max-h-[380px] scrollbar-thin transition-opacity duration-200 ${loadingLogs ? 'opacity-50' : 'opacity-100'}`}>
                                        <table className="w-full text-left border-collapse text-xs">
                                            <thead>
                                                <tr className="bg-slate-50 text-slate-600 font-bold uppercase tracking-wider border-b border-gray-200 text-[10px]">
                                                    <th className="p-3 w-[140px]">Data & Hora</th>
                                                    <th className="p-3 w-[140px]">Usuário</th>
                                                    <th className="p-3 w-[160px]">Ação / Local</th>
                                                    <th className="p-3">Detalhamento da Alteração (De ➔ Para)</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-100">
                                                {logs.map((log) => (
                                                    <tr key={log.id} className="hover:bg-slate-50/80 transition-colors">
                                                        <td className="p-3 font-mono text-[11px] font-bold text-slate-600 whitespace-nowrap">
                                                            {new Date(log.data_hora).toLocaleString('pt-BR')}
                                                        </td>
                                                        <td className="p-3 font-bold text-slate-800">
                                                            <span className="px-2 py-0.5 bg-blue-50 text-sabesp-blue rounded-md border border-blue-100 text-[11px]">
                                                                👤 {log.usuario_nome || 'Sistema'}
                                                            </span>
                                                        </td>
                                                        <td className="p-3 font-semibold text-slate-700">
                                                            <div className="font-extrabold text-slate-800 text-[11px]">{log.pagina_referencia || 'Geral'}</div>
                                                            <div className="text-[10px] text-blue-600 font-mono">{log.tipo_acao || 'ALTERAÇÃO'}</div>
                                                        </td>
                                                        <td className="p-3 font-mono text-[11px] text-gray-600 leading-relaxed break-words max-w-[350px]">
                                                            {log.detalhes || '-'}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                </div>

                {/* Rodapé */}
                <div className="bg-slate-50 px-6 py-4 border-t border-gray-100 flex justify-end shrink-0">
                    <button
                        onClick={onClose}
                        className="px-6 py-2.5 bg-slate-800 text-white font-bold rounded-xl hover:bg-slate-900 transition-all text-xs uppercase tracking-wider"
                    >
                        Concluído
                    </button>
                </div>
            </div>
        </div>
    );
}

export default ConfiguracoesMapaModal;
